import { useCallback } from "react";
import type { Route } from "next";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";

import {
  AccordionMenu,
  AccordionMenuGroup,
  AccordionMenuItem,
  AccordionMenuLabel,
} from "@/components/ui/accordion-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLayout } from "./context";
import { activeMenuPath } from "@/lib/navigation-active";

export function SidebarMenu({ onNavigate }: { onNavigate?: () => void }) {
  const t = useTranslations("layout30");
  const pathname = usePathname();
  const { settings } = useLayout();
  const activePath = activeMenuPath(
    settings.sectionMenu.flatMap(
      (item) => item.children?.map((child) => child.path) ?? [],
    ),
    pathname,
  );
  const matchPath = useCallback(
    (path: string): boolean => path === activePath,
    [activePath],
  );

  return (
    <aside
      data-slot="sidebar-menu-wrapper"
      data-debug-source="components/layouts/layout-30/components/sidebar-menu.tsx"
      aria-label={t(
        settings.audience === "supplier"
          ? "supplierSectionNavigation"
          : "sectionNavigation",
      )}
      className="flex min-h-0 w-0 min-w-0 flex-1 flex-col items-stretch border-e border-border/60 bg-[var(--layout-background)] bg-muted/20 p-3 lg:fixed lg:top-(--header-height) lg:bottom-0 lg:start-(--sidebar-width) lg:z-10 lg:w-(--sidebar-menu-width) lg:flex-none lg:p-3.5"
    >
      <ScrollArea className="-me-2.5 min-h-0 flex-1 pe-2.5">
        <AccordionMenu
          selectedValue={pathname}
          matchPath={matchPath}
          type="multiple"
          className="space-y-5"
          classNames={{
            separator: "-mx-2 mb-2.5",
            label:
              "text-sm font-semibold text-foreground [overflow-wrap:anywhere]",
            item: "h-auto min-h-9 border border-transparent px-2.5 py-2 whitespace-normal [overflow-wrap:anywhere] text-sm font-normal leading-5 text-foreground hover:bg-zinc-100 hover:text-foreground dark:hover:bg-zinc-800/60 dark:hover:text-zinc-100 data-[selected=true]:border-zinc-900 data-[selected=true]:bg-zinc-900 data-[selected=true]:font-semibold data-[selected=true]:text-zinc-50 data-[selected=true]:hover:bg-zinc-800 data-[selected=true]:hover:text-zinc-50 data-[selected=true]:shadow-xs dark:data-[selected=true]:border-[lab(85_0.56_-1.98)] dark:data-[selected=true]:bg-[lab(85_0.56_-1.98)] dark:data-[selected=true]:text-zinc-950 dark:data-[selected=true]:hover:bg-[lab(78_0.56_-1.98)] dark:data-[selected=true]:hover:text-zinc-950 [&[data-selected=true]_svg]:text-zinc-50 dark:[&[data-selected=true]_svg]:text-zinc-950 [&[data-selected=true]_svg]:opacity-100",
            group: "",
          }}
        >
          {settings.sectionMenu.map((item) => (
            <AccordionMenuGroup key={item.title}>
              <AccordionMenuLabel>
                {t(item.title! as Parameters<typeof t>[0])}
              </AccordionMenuLabel>
              {item.children?.map((child) => (
                <AccordionMenuItem
                  key={child.title}
                  value={child.path ?? child.title!}
                  disabled={child.disabled}
                  asChild={Boolean(child.path)}
                >
                  {child.path ? (
                    <Link href={child.path as Route} onClick={onNavigate}>
                      {child.icon ? <child.icon /> : null}
                      <span>{t(child.title! as Parameters<typeof t>[0])}</span>
                    </Link>
                  ) : (
                    <>
                      {child.icon ? <child.icon /> : null}
                      <span>{t(child.title! as Parameters<typeof t>[0])}</span>
                    </>
                  )}
                </AccordionMenuItem>
              ))}
            </AccordionMenuGroup>
          ))}
        </AccordionMenu>
      </ScrollArea>
    </aside>
  );
}
